import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Grid, List, Filter } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { categories, Product } from '../data/products';
import { listProducts } from '../api/products';

const ProductListingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<string>('name');
  const [priceRange, setPriceRange] = useState<{ min: number; max: number }>({ min: 0, max: 10000 });
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 36;

  // Scroll to top when component mounts or route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [searchParams]);

  // Initialize selectedCategory from URL when the route/search params change.
  // This keeps the radio UI in sync when user navigates using a link like /products?category=speakers
  useEffect(() => {
    const c = searchParams.get('category') || '';
    setSelectedCategory(c);
  }, [searchParams]);

  useEffect(() => {
    const category = selectedCategory || undefined;
    const search = searchParams.get('search') || undefined;

    setLoading(true);
    setCurrentPage(1); // Reset to page 1 when filters change
    listProducts({
      category,
      search,
      minPrice: priceRange.min,
      maxPrice: priceRange.max,
      sort: sortBy as any,
      page: 1,
      limit: productsPerPage,
    })
      .then((data) => {
        setFilteredProducts(data.items || []);
        setTotalPages(data.pages || 1);
        setTotalProducts(data.total || 0);
        setCurrentPage(data.page || 1);
      })
      .catch((error) => {
        console.error('Error loading products:', error);
        setFilteredProducts([]);
        setTotalPages(1);
        setTotalProducts(0);
        setCurrentPage(1);
      })
      .finally(() => setLoading(false));
  }, [searchParams, priceRange, sortBy, selectedCategory]);

  const loadPage = async (page: number) => {
    const category = selectedCategory || undefined;
    const search = searchParams.get('search') || undefined;

    setLoading(true);
    listProducts({
      category,
      search,
      minPrice: priceRange.min,
      maxPrice: priceRange.max,
      sort: sortBy as any,
      page,
      limit: productsPerPage,
    })
      .then((data) => {
        setFilteredProducts(data.items || []);
        setTotalPages(data.pages || 1);
        setTotalProducts(data.total || 0);
        setCurrentPage(data.page || 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((error) => {
        console.error('Error loading products:', error);
        setFilteredProducts([]);
        setTotalPages(1);
        setTotalProducts(0);
        setCurrentPage(1);
      })
      .finally(() => setLoading(false));
  };

  const clearFilters = () => {
    setPriceRange({ min: 0, max: 10000 });
    setSelectedCategory('');
    setSortBy('name');
  };

  const categoryName = selectedCategory ? categories.find(cat => cat.id === selectedCategory)?.name : '';
  const pageTitle = categoryName ? `${categoryName} Products` : 'All Products';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={pageTitle}
        description={`Browse our collection of ${categoryName || 'tech accessories'}. Find the best ${categoryName || 'gadgets'} at cnine.`}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {pageTitle}
          </h1>
          <p className="text-gray-600 text-xs sm:text-sm text-right flex-shrink-0">
            {loading ? 'Loading…' : (
              totalProducts === 0 ? 'No products found' : `${totalProducts} product${totalProducts === 1 ? '' : 's'} found`
            )}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          <div className="lg:w-64">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center justify-between mb-1.5">
                <h2 className="text-xs sm:text-sm font-semibold text-gray-900">Filters</h2>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden p-1 text-gray-500 transition-colors"
                  onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                >
                  <Filter className="w-4 h-4" />
                </button>
              </div>

              <div className={`space-y-3 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                <div>
                  <h3 className="text-xs font-medium text-gray-900 mb-1.5">Category</h3>
                  <div className="space-y-1">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="category"
                        value=""
                        checked={selectedCategory === ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedCategory(val);
                          if (val) {
                            searchParams.set('category', val);
                          } else {
                            searchParams.delete('category');
                          }
                          setSearchParams(searchParams);
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">All Categories</span>
                    </label>
                    {categories.map((category) => (
                      <label key={category.id} className="flex items-center">
                        <input
                          type="radio"
                          name="category"
                          value={category.id}
                          checked={selectedCategory === category.id}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSelectedCategory(val);
                            if (val) {
                              searchParams.set('category', val);
                            } else {
                              searchParams.delete('category');
                            }
                            setSearchParams(searchParams);
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{category.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-medium text-gray-900 mb-1.5">Price Range</h3>
                  <div className="space-y-1.5">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, min: Number(e.target.value) || 0 }))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs"
                      />
                      <span className="text-gray-500 text-xs">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, max: Number(e.target.value) || 10000 }))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={clearFilters}
                  className="w-full text-sm font-medium transition-colors"
                  style={{ color: '#4554a4' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#374a8a'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#4554a4'}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
              <div className="flex flex-row justify-between items-center gap-3">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Sort:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1.5 text-xs sm:text-sm focus:border-transparent transition-colors flex-1 min-w-0"
                    onFocus={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(69, 84, 164, 0.2)';
                      e.currentTarget.style.borderColor = '#4554a4';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.boxShadow = '';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                  >
                    <option value="name">Name</option>
                    <option value="price">Price: Low to High</option>
                    <option value="-price">Price: High to Low</option>
                    <option value="rating">Rating</option>
                  </select>
                </div>

                <div className="flex items-center space-x-1.5 flex-shrink-0">
                  <span className="text-xs text-gray-700 hidden sm:inline">View:</span>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? '' : 'text-gray-500'
                      }`}
                    style={viewMode === 'grid' ? {
                      backgroundColor: 'rgba(69, 84, 164, 0.1)',
                      color: '#4554a4'
                    } : {}}
                    onMouseEnter={(e) => {
                      if (viewMode !== 'grid') {
                        e.currentTarget.style.color = '#4554a4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (viewMode !== 'grid') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? '' : 'text-gray-500'
                      }`}
                    style={viewMode === 'list' ? {
                      backgroundColor: 'rgba(69, 84, 164, 0.1)',
                      color: '#4554a4'
                    } : {}}
                    onMouseEnter={(e) => {
                      if (viewMode !== 'list') {
                        e.currentTarget.style.color = '#4554a4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (viewMode !== 'list') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-600">Loading products…</div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Filter className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600">Try adjusting your filters or search terms</p>
              </div>
            ) : (
              <div className={
                viewMode === 'grid'
                  ? 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'
                  : 'space-y-6'
              }>
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <div className="mt-8 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => loadPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * productsPerPage + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * productsPerPage, totalProducts)}
                      </span>{' '}
                      of <span className="font-medium">{totalProducts}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => loadPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        // Show first, last, current, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => loadPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === page
                                ? 'z-10 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                }`}
                              style={currentPage === page ? {
                                backgroundColor: '#4554a4',
                                outlineColor: '#4554a4'
                              } : {}}
                              onMouseEnter={(e) => {
                                if (currentPage !== page) {
                                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (currentPage !== page) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
                            <span
                              key={page}
                              className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                      <button
                        onClick={() => loadPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListingPage;
